import { createStyles, makeStyles } from '@material-ui/styles';
import * as classNames from 'classnames';
import { FC, useEffect, useState } from 'react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { Responsive } from 'src/@context/Responsive';
import mediaQueries, { } from 'src/constants/responsive';
import spacing from 'src/constants/spacing';
import stylesheet, { bodyFontSize } from 'src/constants/stylesheet';
import { getAlt } from 'src/utils/legendAlt';
import Image from './Image';

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      position: 'relative',
      width: '100%',
      [mediaQueries.AfterMobile]: {
        height: '100%',
      }
    },
    figure: {
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      opacity: 0,
      transition: 'opacity linear .5s',
      pointerEvents: 'none',

      '&$active': {
        opacity: 1,
        pointerEvents: 'initial',
      },
    },

    image: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      ...stylesheet.ObjectFitCover
    },

    figcaption: {
      position: 'absolute',
      opacity: 0,
      transition: 'all .5s ease',
      pointerEvents: 'none',

      '$active &': {
        opacity: 1,
        pointerEvents: 'initial',
      },

      [mediaQueries.Mobile]: {
        top: 0,

        '&$active': {
          position: 'relative',
          opacity: 1,
          pointerEvents: 'initial',
        },
      },

      [mediaQueries.AfterMobile]: {
        width: '80%',
        bottom: 0, 
        zIndex: 2,
      },

      [mediaQueries.Tablet]: {
        left: spacing.Gutter.Laptop,

        '$active &': {
          bottom: spacing.Gutter.Laptop
        },
      },
      [mediaQueries.Laptop]: {
        left: spacing.Gutter.Laptop,

        '$active &': {
          bottom: spacing.Gutter.Laptop
        },
      },
      [mediaQueries.Desktop]: {
        left: spacing.Gutter.Desktop,

        '$active &': {
          bottom: spacing.Gutter.Desktop
        },
      },
      [mediaQueries.BigDesktop]: {
        left: spacing.Gutter.BigDesktop,

        '$active &': {
          bottom: spacing.Gutter.BigDesktop
        },
      },
    },
    overTitle: {
      ...stylesheet.BoldText,
      marginBottom: '0.3125rem',
      // [mediaQueries.Mobile]: {
      // }
    },
    title: {
      ...stylesheet.MainTitle,
    },
    subTitle: {
      ...stylesheet.SubTitle,
    },
    subTitleMargin: {
      [mediaQueries.Mobile]: {
        marginTop: `${5 / bodyFontSize.Desktop}rem`,
      },
      [mediaQueries.Tablet]: {
        marginTop: `${5 / bodyFontSize.Tablet}rem`,
      },
      [mediaQueries.Laptop]: {
        marginTop: `${5 / bodyFontSize.Laptop}rem`,
      },
      [mediaQueries.Desktop]: {
        marginTop: `${5 / bodyFontSize.Desktop}rem`,
      },
      [mediaQueries.BigDesktop]: {
        marginTop: `${5 / bodyFontSize.BigDesktop}rem`,
      }
    },

    info: {},

    link: {
      fontSize: 'inherit',
      color: 'inherit',
      pointerEvents: 'none',

      '$active &': {
        pointerEvents: 'initial',
      },

      '&:hover': {
        color: 'inherit'
      },
    },
    whiteFont: {
      color: 'white'
    },
    active: {},
    nav: {
      position: 'absolute',
      zIndex: 1,

      [mediaQueries.Mobile]: {
        right: 0,
      },
      [mediaQueries.Tablet]: {
        right: spacing.Gutter.Tablet,
        bottom: spacing.Gutter.Tablet
      },
      [mediaQueries.Laptop]: {
        right: spacing.Gutter.Laptop,
        bottom: spacing.Gutter.Laptop
      },
      [mediaQueries.Desktop]: {
        right: spacing.Gutter.Desktop,
        bottom: spacing.Gutter.Desktop
      },
      [mediaQueries.BigDesktop]: {
        right: spacing.Gutter.BigDesktop,
        bottom: spacing.Gutter.BigDesktop
      },
    },
    navItem: {
      width: 10,
      height: 10,
      border: 'none',
      marginLeft: 10,
      backgroundColor: 'white',
      borderRadius: '100%',
      padding: 0,
      cursor: 'pointer',

      [mediaQueries.Mobile]: {
        backgroundColor: 'rgba(0,0,0,.4)',
      }
    },
    navItemActive: {
      backgroundColor: 'black',
    },
    contentMobile: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
    },
    mobileFigures: {
      position: 'relative',
      height: 0,
      overflow: 'hidden',
      paddingBottom: '66.66%',
      marginBottom: 15,
    },
    mobileLegends: {
      position: 'relative'
    },
    linkImage: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1,
    }
  }));

export type DiaporamaProps = { items: NewsItemDto[] };

type Props = DiaporamaProps;

const Diaporama: FC<Props> = ({ items }) => {
  const cls = useStyles();
  const [active, setActive] = useState<number>();

  useEffect(() => {
    if (!active) {
      setActive(0);
    }
  }, [])

  useEffect(() => {
    const timeOut = setTimeout(changeActive, 6000);

    return (() => clearTimeout(timeOut));
  }, [active])

  const changeActive = () => {
    if (active === undefined) { return };
    const newActive = (active + 1) % items.length;
    setActive(newActive);
  }

  const renderLink = (link: string, legend: string) => {
    const isExternal = link.startsWith('/');
    if (isExternal) {
      return <Link to={link} className={cls.link}>
        {legend}
      </Link>
    }
    if (!isExternal) {
      return <a href={link} className={cls.link} target="_blank">{legend}</a>
    }
    return null;
  };

  return (
    <div className={cls.root}>
      <Responsive steps={['AfterMobile']}>
        {
          items.map((item, i) =>
            <div
              key={item.UID}
              className={
                classNames(cls.figure, item.TextWhite && cls.whiteFont, active === i && cls.active)
              }
            >

              {item.Link && item.Link.startsWith('/') &&
                <Link to={item.Link} className={cls.linkImage} />
              }

              {item.Link && !item.Link.startsWith('/') &&
                <a href={item.Link} className={cls.linkImage} target="_blank" />
              }

              <Image
                pictureRef={item.Image}
                sizes={'100vw'}
                className={cls.image}
                alt={getAlt(item.Image)}
              />

              <div className={cls.figcaption}>
                <h5 className={cls.overTitle}>
                  {item.OverTitle}
                </h5>
                <h1 className={cls.title}>
                  {item.Link && renderLink(item.Link, item.Title)}
                  {!item.Link && <>{item.Title} </>}
                </h1>
                <h2 className={classNames(cls.subTitle, cls.subTitleMargin)}>
                  {item.Link ?
                    renderLink(item.Link, item.Subtitle) :
                    <>{item.Subtitle} </>}
                </h2>
                <div className={cls.info} dangerouslySetInnerHTML={{ __html: item.Info }} />
              </div>
            </div>
          )}
        <nav className={cls.nav}>
          {items.map((item, i) =>
            <button
              key={`button-${item.UID}`}
              className={
                classNames(
                  cls.navItem,
                  active === i && cls.navItemActive
                )
              }
              // tslint:disable-next-line:jsx-no-lambda
              onClick={() => setActive(i)}
            />
          )}
        </nav>
      </Responsive>
      <Responsive steps={['Mobile']}>
        <div className={cls.contentMobile}>
          <div className={cls.mobileFigures}>
            {items.map((item, i) =>
              <div
                className={
                  classNames(cls.figure, item.TextWhite && cls.whiteFont, active === i && cls.active)
                }
                key={`mobile-image-${item.UID}`}
              >
                {item.Link &&
                  <Link to={item.Link} className={cls.linkImage} />
                }
                <Image
                  pictureRef={item.Image}
                  sizes={'100vw'}
                  className={cls.image}
                  alt={getAlt(item.Image)}
                />
              </div>
            )}
          </div>
          <div className={cls.mobileLegends}>
            <nav className={cls.nav}>
              {items.map((item, i) =>
                <button
                  key={`button-${item.UID}`}
                  className={
                    classNames(
                      cls.navItem,
                      active === i && cls.navItemActive
                    )
                  }
                  // tslint:disable-next-line:jsx-no-lambda
                  onClick={() => setActive(i)}
                />
              )}
            </nav>
            {items.map((item, i) =>
              <div
                key={`mobile-legend-${item.UID}`}
                className={
                  classNames(cls.figcaption, active === i && cls.active)
                }
              >
                <h5 className={cls.overTitle}>
                  {item.OverTitle}
                </h5>
                <h1 className={cls.title}>
                  {item.Link ?
                    <Link to={item.Link} className={cls.link}>
                      {item.Title}
                    </Link> :
                    <>{item.Title} </>}
                </h1>
                <h2 className={classNames(cls.subTitle, cls.subTitleMargin)}>
                  {item.Link ?
                    <Link to={item.Link} className={cls.link}>
                      {item.Subtitle}
                    </Link> :
                    <>{item.Subtitle} </>}
                </h2>
                <div className={cls.info} dangerouslySetInnerHTML={{ __html: item.Info }} />
              </div>
            )}
          </div>
        </div>
      </Responsive>
    </div>
  );
};

export default Diaporama;


// WEBPACK FOOTER //
// ./src/Components/Diaporama.tsx